import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Barcode, Camera, Check, Loader2, ScanBarcode, Sparkles, Plus, X } from "lucide-react";
import SuggestReleaseDialog from "./SuggestReleaseDialog";
import AdminReleaseForm from "./AdminReleaseForm";
import { BRANDS } from "../lib/brands";

function resizeImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    reader.readAsDataURL(file);
  });
}

// Best-effort guess of brand from the AI product_hint (looks for known brand tokens).
// Match against the full shared BRANDS list so newly-added brands are auto-recognized.
// Sort longest-first so multi-word matches (e.g. "Mini GT") win over substrings.
const BRAND_TOKENS_SORTED = [...BRANDS].filter((b) => b !== "Other").sort((a, b) => b.length - a.length);
function guessBrandFromHint(hint) {
  if (!hint) return "";
  const lower = hint.toLowerCase();
  for (const b of BRAND_TOKENS_SORTED) {
    if (lower.includes(b.toLowerCase())) return b;
  }
  return "";
}

// Strip the brand token + common licensing boilerplate off the hint to get a cleaner release name.
const BOILERPLATE_PATTERNS = [
  /\(toyota official licensed product\)/i,
  /\(nissan official licensed product\)/i,
  /\(honda official licensed product\)/i,
  /\(mazda official licensed product\)/i,
  /\(subaru official licensed product\)/i,
  /\(officially licensed[^)]*\)/i,
  /\(made in china\)/i,
  /\(made in vietnam\)/i,
  /\bofficial licensed product\b/i,
  /\bmade in (china|vietnam|thailand|malaysia)\b/i,
];
function guessNameFromHint(hint, brand) {
  if (!hint) return "";
  let cleaned = hint;
  if (brand) {
    const re = new RegExp(brand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
    cleaned = cleaned.replace(re, "");
  }
  for (const pat of BOILERPLATE_PATTERNS) cleaned = cleaned.replace(pat, "");
  return cleaned.replace(/\s{2,}/g, " ").trim().replace(/^[\s\-:|·]+|[\s\-:|·]+$/g, "");
}

// Vehicle guess: scan for known vehicle tokens (model name) in the hint.
const VEHICLE_TOKENS = [
  "Supra", "GT-R", "GTR", "Skyline", "Silvia", "180SX", "240SX", "S13", "S14", "S15",
  "RX-7", "RX7", "RX-8", "Civic", "NSX", "Integra", "S2000", "Mustang", "Camaro",
  "Corvette", "Charger", "Challenger", "Datsun", "510", "240Z", "300ZX", "Z32",
  "Lancer Evo", "Evo", "WRX", "STI", "Impreza", "BRZ", "86", "GR Yaris", "GR86",
  "Mark II", "Cresta", "Chaser", "JZX100", "JZX90", "AE86", "Trueno", "Levin",
  "Land Cruiser", "Hilux", "Jimny", "Pao", "Be-1", "Figaro", "Cube",
  "Lancia", "Delta", "Stratos", "F40", "F50", "Testarossa", "458", "488", "F8",
  "911", "Cayman", "Boxster", "M3", "M5", "E30", "E36", "E46", "Golf", "GTI",
  "Camaro", "Mustang GT", "Charger Hellcat", "Challenger SRT", "Cybertruck",
];
function guessVehicleFromHint(hint) {
  if (!hint) return "";
  const MAKES = "toyota|nissan|honda|mazda|subaru|mitsubishi|ford|chevrolet|chevy|dodge|ferrari|porsche|bmw|mercedes|audi|lamborghini|lexus|datsun|vw|volkswagen";
  for (const v of VEHICLE_TOKENS) {
    const safeV = v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const modelRe = new RegExp(`\\b${safeV}\\b`, "i");
    if (!modelRe.test(hint)) continue;
    // 1) Strict adjacency: "Toyota Supra"
    const adjRe = new RegExp(`(${MAKES})\\s+${safeV}`, "i");
    const adj = hint.match(adjRe);
    if (adj) return adj[0].replace(/\s+/g, " ").trim();
    // 2) Any-position make + model anywhere in the hint (e.g. "TOP SECRET ... SUPRA ... (Toyota ...)")
    const makeRe = new RegExp(`\\b(${MAKES})\\b`, "i");
    const makeMatch = hint.match(makeRe);
    if (makeMatch) {
      const make = makeMatch[1];
      const cap = make.charAt(0).toUpperCase() + make.slice(1).toLowerCase();
      const niceMake = cap === "Vw" ? "Volkswagen" : cap === "Chevy" ? "Chevrolet" : cap;
      // Skip prefix if the model token itself is already the make (e.g. "Datsun" model + "Datsun" make)
      if (niceMake.toLowerCase() === v.toLowerCase()) return v;
      return `${niceMake} ${v}`;
    }
    // 3) Model alone if no make is mentioned
    return v;
  }
  return "";
}

export default function BarcodeScanDialog({ trigger, onCollectionChange, onCatalogChange, onAddAsCustom }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [result, setResult] = useState(null);
  const [adding, setAdding] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [adminFormOpen, setAdminFormOpen] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const reset = () => {
    setPreviewUrl(null);
    setResult(null);
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) reset();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use a JPEG, PNG, or WEBP image");
      return;
    }
    setScanning(true);
    setResult(null);
    try {
      const dataUrl = await resizeImage(file);
      setPreviewUrl(dataUrl);
      toast.info("Reading barcode...");
      const { data } = await api.post("/catalog/scan-barcode", { image_base64: dataUrl });
      setResult(data);
      if (!data.barcode?.upc && !data.candidates?.length) {
        toast.warning("No barcode found. Try a sharper photo with the barcode clearly visible.");
      } else if (data.candidates?.length) {
        toast.success(`Matched ${data.candidates.length} catalog release${data.candidates.length > 1 ? "s" : ""}`);
      } else {
        toast.info(`Barcode ${data.barcode.upc} read — no catalog match yet.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleAdd = async (release) => {
    setAdding(release.release_id);
    try {
      await api.post("/collection", { release_id: release.release_id });
      toast.success(`Added ${release.release_name}`);
      onCollectionChange?.();
      handleOpenChange(false);
      navigate(`/catalog/${release.release_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(null);
    }
  };

  const barcode = result?.barcode;
  const candidates = result?.candidates || [];

  // Build prefill from AI scan data. Prefer the AI-returned brand/vehicle (server-side GPT extraction),
  // and only fall back to client-side string matching when the model didn't identify them.
  const guessedBrand = (barcode?.brand || "").trim() || guessBrandFromHint(barcode?.product_hint);
  const guessedName = guessNameFromHint(barcode?.product_hint, guessedBrand);
  const guessedVehicle = (barcode?.vehicle || "").trim() || guessVehicleFromHint(barcode?.product_hint);
  const suggestPrefill = {
    brand: guessedBrand,
    release_name: guessedName,
    vehicle: guessedVehicle,
    upc: barcode?.upc || "",
    notes: barcode?.product_hint ? `Auto-extracted from barcode scan. Hint: "${barcode.product_hint}"` : "",
  };
  const adminPrefill = {
    brand: guessedBrand,
    release_name: guessedName,
    vehicle: guessedVehicle,
    upc: barcode?.upc || "",
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {trigger || (
            <Button
              data-testid="barcode-scan-btn"
              variant="outline"
              size="sm"
              className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-sky-500/40"
            >
              <ScanBarcode className="w-3.5 h-3.5 mr-1.5" /> Scan Barcode
            </Button>
          )}
        </DialogTrigger>
        <DialogContent
          data-testid="barcode-scan-dialog"
          className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-zinc-100 flex items-center gap-2">
              <Barcode className="w-4 h-4 text-sky-400" /> Scan Barcode
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Snap the UPC/EAN on the box or blister pack. AI reads the digits and looks for a catalog match — if it&apos;s new, you can add it on the spot.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3">
            <div className="rounded-xl border border-dashed border-zinc-800 bg-zinc-900/40 p-4 flex flex-col items-center gap-3">
              {previewUrl ? (
                <div className="relative w-full">
                  <img src={previewUrl} alt="Barcode preview" className="w-full rounded-lg border border-zinc-800 max-h-48 object-contain bg-black" />
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="clear-barcode-preview"
                    className="absolute -top-2 -right-2 bg-zinc-950 border border-zinc-700 rounded-full p-1 text-zinc-400 hover:text-sky-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-full py-6 flex flex-col items-center gap-2 text-zinc-500">
                  <Barcode className="w-10 h-10 text-zinc-700" />
                  <p className="text-xs">UPC-A, EAN-13, UPC-E, EAN-8 all work.</p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={handleFile}
                className="hidden"
                data-testid="barcode-file-input"
              />
              <Button
                data-testid="barcode-file-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={scanning}
                className="bg-sky-600 hover:bg-sky-500 text-white"
              >
                {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                {scanning ? "Reading..." : previewUrl ? "Try another photo" : "Pick a photo"}
              </Button>
            </div>
          </div>

          {barcode && (
            <div data-testid="barcode-result" className="mt-3 p-3 rounded-xl bg-zinc-900/60 border border-sky-500/20 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-sky-500/10 text-sky-400 flex-shrink-0">
                <Barcode className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-sky-400/80 uppercase tracking-wider font-mono">{barcode.format || "UPC"}</p>
                <p data-testid="barcode-digits" className="font-mono text-sm text-zinc-100 truncate">
                  {barcode.upc || "No digits detected"}
                </p>
                {barcode.product_hint && (
                  <p className="text-[11px] text-zinc-500 truncate">
                    Hint: {barcode.product_hint}
                    {barcode.brand && (
                      <> · <span className="text-purple-300/80">{barcode.brand}</span></>
                    )}
                    {barcode.vehicle && (
                      <> · <span className="text-sky-300/80">{barcode.vehicle}</span></>
                    )}
                  </p>
                )}
              </div>
              <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border flex-shrink-0 ${
                barcode.confidence === "high" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" :
                barcode.confidence === "medium" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" :
                "bg-zinc-800 text-zinc-500 border-zinc-700"
              }`}>{barcode.confidence}</span>
            </div>
          )}

          {candidates.length > 0 && (
            <div className="mt-3 space-y-2">
              <h3 className="text-[11px] text-zinc-500 uppercase tracking-wider font-mono">Matches</h3>
              {candidates.map((r) => (
                <div key={r.release_id} data-testid={`barcode-candidate-${r.release_id}`} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800 hover:border-sky-500/30 rounded-xl p-3 transition-colors">
                  <div className="w-14 h-14 rounded bg-zinc-950 border border-zinc-800 overflow-hidden flex-shrink-0">
                    {r.model_image ? (
                      <img src={resolveImageUrl(r.model_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[8px] text-zinc-600 font-mono">{r.khmg_number || "—"}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-100 truncate">{r.release_name}</p>
                    <p className="text-[10px] text-zinc-500 font-mono truncate">
                      {r.khmg_number || "—"} · {r.vehicle || ""} · <span className="text-sky-400/70">{r.match_reason === "upc" ? "UPC match" : `match ${Math.round((r.match_score || 0) * 100)}%`}</span>
                    </p>
                  </div>
                  {r.owned ? (
                    <span className="text-[10px] text-emerald-400 font-mono uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 flex-shrink-0">Owned</span>
                  ) : (
                    <Button
                      data-testid={`barcode-add-${r.release_id}`}
                      size="sm"
                      onClick={() => handleAdd(r)}
                      disabled={adding === r.release_id}
                      className="bg-sky-600 hover:bg-sky-500 text-white flex-shrink-0"
                    >
                      {adding === r.release_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                      Add
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* "Add this instead" CTA — shows whenever AI pulled out a product hint, even if it
               also returned low-confidence catalog matches. Lets the user override and add the
               actual product they scanned, not whatever the fuzzy matcher thought was closest. */}
          {result && barcode?.product_hint && (
            <div data-testid="barcode-no-match" className="mt-3 rounded-xl bg-gradient-to-br from-zinc-900/60 to-purple-950/10 border border-dashed border-purple-500/25 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-400 flex-shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-100">
                    {candidates.length > 0 ? "Not the right match?" : "Not in the catalog yet"}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                    AI read the packaging as
                    {" "}<span className="text-zinc-300">&ldquo;{barcode.product_hint}&rdquo;</span>.{" "}
                    {isAdmin
                      ? "Add it to the catalog so it appears for everyone."
                      : "Submit it for review — once an admin approves, it joins the catalog and your future scans match instantly."}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {isAdmin ? (
                  <Button
                    data-testid="barcode-admin-add-catalog"
                    size="sm"
                    onClick={() => setAdminFormOpen(true)}
                    className="bg-rose-600 hover:bg-rose-500 text-white"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add this to Catalog
                  </Button>
                ) : (
                  <Button
                    data-testid="barcode-suggest-catalog"
                    size="sm"
                    onClick={() => setSuggestOpen(true)}
                    className="bg-purple-600 hover:bg-purple-500 text-white"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Suggest this Release
                  </Button>
                )}
                {onAddAsCustom && (
                  <Button
                    data-testid="barcode-add-as-custom-btn"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Build the prefill payload from the AI/barcode result.
                      // Mirror the QuickScan handoff so the user lands on the
                      // same Add Custom Item flow regardless of how they
                      // arrived. UPC is captured so the custom entry stays
                      // recognizable on future scans.
                      const prefill = {
                        ...(guessedBrand ? { brand: guessedBrand } : {}),
                        ...(guessedName ? { release_name: guessedName } : {}),
                        ...(guessedVehicle ? { vehicle: guessedVehicle } : {}),
                        ...(barcode?.upc ? { upc: barcode.upc } : {}),
                        ...(barcode?.product_hint
                          ? { notes: `Auto-extracted from barcode scan. UPC: ${barcode?.upc || "n/a"} · Hint: "${barcode.product_hint}"` }
                          : {}),
                      };
                      const photo = previewUrl;
                      handleOpenChange(false);
                      onAddAsCustom(prefill, photo);
                    }}
                    className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-rose-500/40"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add as Custom Item
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Hard fallback: AI returned nothing useful at all */}
          {result && !barcode?.product_hint && !barcode?.upc && candidates.length === 0 && (
            <div data-testid="barcode-blank-result" className="mt-3 p-3 rounded-xl bg-zinc-900/40 border border-dashed border-zinc-800 text-xs text-zinc-500">
              AI couldn&apos;t read the packaging. Try a sharper, well-lit photo with the barcode and product label clearly visible.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Controlled child dialogs — open when no-match CTA is clicked */}
      <SuggestReleaseDialog
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        prefill={suggestPrefill}
        onSubmitted={() => {
          handleOpenChange(false);
        }}
      />
      <AdminReleaseForm
        open={adminFormOpen}
        onOpenChange={setAdminFormOpen}
        prefill={adminPrefill}
        onSuccess={() => {
          onCatalogChange?.();
          handleOpenChange(false);
          toast.success("Added to catalog!");
        }}
      />
    </>
  );
}
